/**
 * @file 云钱包领取质押奖励相关hook
 */
import { useState, useRef, useCallback, useEffect } from 'react';
import { ClaimOption, Process, RewardType, ClaimType } from '@/types/claim';
import { useCoinInfoContext } from '@/hooks/useCoinInfo';
import { postRewardClaim, getClaimFee } from '@/services/claim';
import BigNumber from 'bignumber.js';
import { debounce } from 'lodash-es';
import { ethAddress } from '@/config/address';
import { GasFeeInfo } from '@/types/common';
import { scTrack } from '@web3/base-tracking';
import { Event } from '@/config/track-config';
import { FeeLevel } from '@/types/common';
import { useFm } from '@web3/base-hooks';
import { useToast } from '@web3/base-hooks';

/**
 * gasfee相关hook
 */
export const useGasFee = (option: Partial<ClaimOption>) => {
  const { process, poolInfo, hasReward, rewardFunds } = option as ClaimOption;
  const [gasFeeLevel, setGasFeeLevel] = useState(FeeLevel.Middle);
  const [gasFeeInfo, setGasFeeInfo] = useState<Partial<GasFeeInfo>>({});
  const [gasFeeIsLoading, setGasFeeIsLoading] = useState(false);
  const { coinInfoStore } = useCoinInfoContext();
  const [loopPercent, setLoopPercent] = useState(0);
  const uid = useRef(0);
  const { toastWarn } = useToast();
  const selectedGasFeeInfo = gasFeeInfo[gasFeeLevel];
  const timer = useRef<NodeJS.Timeout>();
  const gasToken = coinInfoStore?.[`${poolInfo?.chainCode}${ethAddress}`];
  const gasFeePrice = BigNumber(selectedGasFeeInfo?.gasFeeMax || 0)
    .times(gasToken?.price)
    .toString();
  const gasStore = {
    hasGasFee: !!selectedGasFeeInfo?.gasFeeMax,
    level: gasFeeLevel,
    amount: selectedGasFeeInfo?.gasFeeMax || '0',
    price: gasFeePrice,
    gasPrice: selectedGasFeeInfo?.gasPrice || '0',
    max: gasFeeInfo?.gasMax || '0',
    estimateFee: selectedGasFeeInfo?.gasFee || '0',
    priority: selectedGasFeeInfo?.priority || '0',
    symbol: gasToken?.symbol || '',
    isLoading: gasFeeIsLoading,
    limit: gasFeeInfo.gasLimit || '',
    loopPercent: loopPercent * 20,
  };

  const fetchGasFee = async (selfUid: number) => {
    setGasFeeIsLoading(true);

    const { success, result, message } = await getClaimFee({
      poolId: poolInfo.id,
      poolCode: poolInfo.poolCode,
      poolType: poolInfo.poolType,
      slippage: '0.5',
      level: gasFeeLevel,
    });

    if (selfUid !== uid.current) return;

    setGasFeeIsLoading(false);

    if (!success) {
      toastWarn(message);
      return false;
    }

    setGasFeeInfo(result.gas);

    return true;
  };

  const startLoopGasFee = useCallback(
    debounce(async (percent = 0) => {
      clearTimeout(timer.current);

      setLoopPercent(percent);

      if (percent === 0) {
        uid.current = Math.random();
        const result = await fetchGasFee(uid.current);
        // 报错则取消轮循
        if (!result) return;
      }

      timer.current = setTimeout(() => {
        if (percent === 4) {
          setLoopPercent(5);
          startLoopGasFee(0);
        } else {
          startLoopGasFee(percent + 1);
        }
      }, 2000);
    }, 500),
    [rewardFunds]
  );

  const onChangeGasFeeLevel = (gasFeeLevel: FeeLevel) => {
    setGasFeeLevel(gasFeeLevel);
  };

  useEffect(() => {
    if (process === Process.None) {
      clearTimeout(timer.current);
    }

    return () => {
      clearTimeout(timer.current);
      uid.current = 0;
    };
  }, [process, startLoopGasFee]);

  useEffect(() => {
    if (hasReward) {
      startLoopGasFee();
    }
  }, [hasReward]);

  return {
    gasFeeInfo,
    gasStore,
    startLoopGasFee,
    onChangeGasFeeLevel,
  };
};

/**
 * 开始领取奖励相关hook
 */
export const useClaim = (option: Partial<ClaimOption>) => {
  const { rewardValue, gasStore, poolInfo, claimType, onChangeProcess } =
    option as ClaimOption;
  const [confirmButtonIsLoading, setConfirmButtonIsLoading] = useState(false);
  const [orderCode, setOrderCode] = useState('');
  const gasFeeIsGreatThanProfit = BigNumber(rewardValue).lt(gasStore.price);
  const { toastWarn } = useToast();

  const beginClaim = async () => {
    scTrack(Event.Web3UserClick, {
      page_name:
        claimType === ClaimType.Claim
          ? 'StakingWeb_bbSOLrewards'
          : 'StakingWeb_bbSOLPagewithdrawal',
      button_name:
        claimType === ClaimType.Claim ? 'rewards_confirm' : 'principal_confirm',
    });

    setConfirmButtonIsLoading(true);
    const { success, result, message } = await postRewardClaim({
      poolId: poolInfo.id,
      poolCode: poolInfo.poolCode,
      poolType: poolInfo.poolType,
      gasLimit: BigNumber(gasStore.limit).toNumber(),
      priority: BigNumber(gasStore.priority).toNumber(),
      gasMax: BigNumber(gasStore.max).toNumber(),
      baseFee: BigNumber(gasStore.gasPrice).toNumber() ?? 0,
      maxPriorityFeePerGas: BigNumber(gasStore.priority).toNumber() ?? 0,
    });
    setConfirmButtonIsLoading(false);

    if (!success) {
      toastWarn(message);
      return;
    }

    if (success) {
      onChangeProcess(Process.Result);
      setOrderCode(result);
    }
  };

  return {
    orderCode,
    confirmButtonIsLoading,
    gasFeeIsGreatThanProfit,
    beginClaim,
  };
};

/**
 * 领取奖励按钮相关hook
 */
export const useButtonStatus = (option: Partial<ClaimOption>) => {
  const fm = useFm();
  const { balance, rewardType, gasStore, claimType } = option as ClaimOption;
  const buttonText =
    claimType === ClaimType.Claim ? fm('claim') : fm('principalWithdrawal');

  const errorText = (() => {
    if (BigNumber(gasStore.amount || 0).gt(balance)) {
      return fm('token_balance_due', { token: gasStore.symbol });
    }
  })();

  const buttonDisabled = (() => {
    if (rewardType === RewardType.None || rewardType === RewardType.Queue) {
      return true;
    }

    if (BigNumber(gasStore.price || 0).gt(balance)) {
      return true;
    }

    if (!gasStore.hasGasFee) {
      return true;
    }
  })();

  return {
    errorText,
    buttonText,
    buttonDisabled,
  };
};
